import { useState } from 'react';
import { Vendor, VendorCreate, VendorUpdate } from '../types/costwand.d';
import { useVendors } from '../hooks/useVendors.ts';
import VendorForm from '../components/VendorForm.tsx';
import VendorTable from '../components/VendorTable.tsx';
import { Button } from '../components/ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '../components/ui/dialog.tsx';

const VendorsPage = () => {
  const {
    vendors,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    handlePageChange,
    createVendor,
    updateVendor,
    deleteVendor,
    fetchVendors,
  } = useVendors();

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
  const [formSubmitting, setFormSubmitting] = useState<boolean>(false);

  const handleAddVendor = () => {
    setEditingVendor(null);
    setIsFormOpen(true);
  };

  const handleEditVendor = (vendor: Vendor) => {
    setEditingVendor(vendor);
    setIsFormOpen(true);
  };

  const handleDeleteVendor = async (id: number) => {
    await deleteVendor(id);
    fetchVendors(currentPage);
  };

  const handleFormSubmit = async (data: VendorCreate | VendorUpdate) => {
    setFormSubmitting(true);

    try {
      let success = false;
      if (editingVendor) {
        success = await updateVendor(editingVendor.id!, data as VendorUpdate);
      } else {
        success = await createVendor(data as VendorCreate);
      }

      if (success) {
        setIsFormOpen(false);
        fetchVendors(currentPage);
      }
    } catch (e: any) {
      // Error handling is managed by the global error context
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setEditingVendor(null);
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold text-secondary-foreground">Vendor Management</h2>
        <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
          <DialogTrigger asChild>
            <Button onClick={handleAddVendor}>Add New Vendor</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>{editingVendor ? 'Edit Vendor' : 'Add New Vendor'}</DialogTitle>
              <DialogDescription>
                {editingVendor ? 'Make changes to the vendor here.' : 'Add a new vendor to your list.'}
              </DialogDescription>
            </DialogHeader>
            <VendorForm
              initialData={editingVendor}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
              isSubmitting={formSubmitting}
              entityId="vendors"
            />
          </DialogContent>
        </Dialog>
      </div>

      <VendorTable
        vendors={vendors}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={handlePageChange}
        onEdit={handleEditVendor}
        onDelete={handleDeleteVendor}
        loading={loading}
        error={error}
        entityId="vendors"
        baseEndpoint="/vendors" // NEW: Pass the base endpoint
      />
    </div>
  );
};

export default VendorsPage;